import React from 'react';
import { Coffee, Users, Clock, Sofa } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const CommonAreas: React.FC = () => {
  // Mock data - in real app this would come from sensors
  const commonAreasData = {
    currentOccupancy: {
      cafeteria: 45,
      mainLounge: 12,
      gameRoom: 4,
      outdoorTerrace: 8
    },
    peakTimes: {
      cafeteria: '12:00 PM - 1:30 PM',
      mainLounge: '3:00 PM - 4:00 PM',
      gameRoom: '12:30 PM - 1:30 PM',
      outdoorTerrace: '11:30 AM - 2:00 PM'
    },
    utilization: {
      cafeteria: 75,
      mainLounge: 45,
      gameRoom: 30,
      outdoorTerrace: 40
    },
    status: [
      { area: 'Main Cafeteria', capacity: 120, current: 45, status: 'Moderate' },
      { area: 'Coffee Bar', capacity: 30, current: 8, status: 'Low' },
      { area: 'Main Lounge', capacity: 40, current: 12, status: 'Low' },
      { area: 'Game Room', capacity: 20, current: 4, status: 'Low' },
      { area: 'Outdoor Terrace', capacity: 60, current: 8, status: 'Low' },
      { area: 'Collaboration Zone', capacity: 35, current: 22, status: 'Moderate' }
    ],
    popularTimes: [
      { time: '9AM', usage: 20 },
      { time: '10AM', usage: 35 },
      { time: '11AM', usage: 45 },
      { time: '12PM', usage: 85 },
      { time: '1PM', usage: 90 },
      { time: '2PM', usage: 60 },
      { time: '3PM', usage: 40 },
      { time: '4PM', usage: 30 },
      { time: '5PM', usage: 15 }
    ]
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'moderate':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Common Areas</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Coffee className="text-brown-500 mr-2" />
            <h3 className="text-lg font-semibold">Cafeteria Usage</h3>
          </div>
          <p className="text-3xl font-bold">{commonAreasData.utilization.cafeteria}%</p>
          <p className="text-gray-500">Current utilization</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Sofa className="text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold">Lounge Areas</h3>
          </div>
          <p className="text-3xl font-bold">{commonAreasData.utilization.mainLounge}%</p>
          <p className="text-gray-500">Current utilization</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Users className="text-purple-500 mr-2" />
            <h3 className="text-lg font-semibold">Total Occupancy</h3>
          </div>
          <p className="text-3xl font-bold">89</p>
          <p className="text-gray-500">People in common areas</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Clock className="text-green-500 mr-2" />
            <h3 className="text-lg font-semibold">Peak Time</h3>
          </div>
          <p className="text-3xl font-bold">12:30</p>
          <p className="text-gray-500">Busiest time today</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Area Status</h3>
          <div className="space-y-4">
            {commonAreasData.status.map((area, index) => (
              <div key={index} className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{area.area}</p>
                  <p className="text-sm text-gray-500">
                    {area.current} / {area.capacity} people
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm ${getStatusColor(area.status)}`}>
                  {area.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Popular Times</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={commonAreasData.popularTimes}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="usage" fill="#8884d8" name="Occupancy %" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default CommonAreas; 